import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { invalidateProjectListQueries } from '@/lib/cache-utils';

// ============================================================================
// Query Hooks (Phase 5 Batch 3: Project Scopes)
// ============================================================================

/**
 * Fetches all scopes for a specific project
 */
export function useProjectScopes(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-scopes', projectId],
    queryFn: () => api.projectScopes.list(projectId!),
    enabled: !!projectId,
    staleTime: 30_000, // 30s — prevent aggressive refetch on remount
  });
}

/**
 * R5 (v0.14): Per-scope invoice summary for the BillingTracksPanel.
 *
 * Returns one row per project_scope with org/DBA names + invoice aggregates
 * (last_invoiced_at, total_invoice_count, sent_invoice_count).
 * `total_invoice_count === 0` is the [Remove] gate for never-used tracks.
 *
 * Kept as a separate query (not folded into useProjectScopes) so the cost is
 * only paid when the panel actually renders (>=2 scopes).
 */
export function useProjectScopeSummary(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-scope-summary', projectId],
    queryFn: () => api.projectScopes.summary(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

// ============================================================================
// Mutation Hooks (Phase 5 Batch 3: Scope CRUD operations)
// ============================================================================

/**
 * Creates a new project scope
 */
export function useCreateProjectScope() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { projectId: string; organizationId: string; scopeTypeId: string; contactId?: string; status?: string; statusNotes?: string }) =>
      api.projectScopes.create(data),
    onSuccess: (response, variables) => {
      // Invalidate scopes for this project
      queryClient.invalidateQueries({ queryKey: ['project-scopes', variables.projectId] });
      toast.success('Scope added successfully');
      return response;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add scope');
    },
  });
}

/**
 * Updates a project scope
 *
 * Issue #13: Updated to support full scope editing (all fields, not just status)
 */
export function useUpdateProjectScope() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      projectId,
      data
    }: {
      id: string;
      projectId: string;
      data: {
        organization_id?: string;
        scope_type_id?: string;
        contact_id?: string | null;
        status?: string;
        status_notes?: string; // Issue #13: Use snake_case to match API
        // v0.12.22 Fix 2: allow updating the scope's default establishment
        establishment_id?: number | null;
      };
    }) => api.projectScopes.update(projectId, id, data),
    onSuccess: (response, variables) => {
      // Invalidate scopes for this project
      queryClient.invalidateQueries({ queryKey: ['project-scopes', variables.projectId] });
      // v0.12.22 Fix 2: also refresh any invoice detail queries — the
      // scope_establishment_id JOIN on get_invoice_by_id needs to reflect
      // the new project default.
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('Scope updated successfully');
      return response;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update scope');
    },
  });
}

/**
 * Deletes a project scope
 *
 * R5 (v0.14): Also invalidates 'project-scope-summary' so the
 * BillingTracksPanel refreshes after a [Remove] action.
 */
export function useDeleteProjectScope() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, projectId }: { id: string; projectId: string }) =>
      api.projectScopes.delete(projectId, id),
    onSuccess: (response, variables) => {
      // Invalidate scopes for this project
      queryClient.invalidateQueries({ queryKey: ['project-scopes', variables.projectId] });
      // R5 (v0.14): refresh BillingTracksPanel summary too
      queryClient.invalidateQueries({ queryKey: ['project-scope-summary', variables.projectId] });
      toast.success('Scope deleted successfully');
      return response;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete scope');
    },
  });
}

/**
 * Phase 9.7bc: Sets a scope as the primary client scope
 * Only one scope can be primary per project - setting a new primary unsets the previous one
 */
export function useSetPrimaryScope() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ scopeId, projectId }: { scopeId: string; projectId: string }) =>
      api.projectScopes.setPrimary(projectId, scopeId),
    onSuccess: (response, variables) => {
      // Invalidate scopes for this project to refresh is_primary flags
      queryClient.invalidateQueries({ queryKey: ['project-scopes', variables.projectId] });
      // Update client_name in detail cache directly from response — avoids
      // triggering a full refetch that could overwrite optimistic updates to
      // other fields (e.g., project_understanding edited concurrently)
      const scope = response?.scope;
      if (scope) {
        queryClient.setQueryData(['projects', 'detail', variables.projectId], (old: any) => {
          if (!old?.project) return old;
          return {
            ...old,
            project: {
              ...old.project,
              client_name: scope.organization_name ?? old.project.client_name,
            },
          };
        });
      }
      // Invalidate project list to update client_name in list view (exclude detail)
      invalidateProjectListQueries(queryClient);
      toast.success('Primary client updated');
      return response;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to set primary client');
    },
  });
}

// ============================================================================
// Phase 9.7bc: Scope Contact Mutations (Multiple contacts per scope)
// ============================================================================

/**
 * Adds a contact to a project scope
 */
export function useAddScopeContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, scopeId, contactId, isPrimaryContact, isInvoiceContact }: {
      projectId: string;
      scopeId: string;
      contactId: string;
      isPrimaryContact?: boolean;
      isInvoiceContact?: boolean;
    }) => api.scopeContacts.add(projectId, scopeId, {
      contact_id: contactId,
      is_primary_contact: isPrimaryContact,
      is_invoice_contact: isInvoiceContact,
    }),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project-scopes', variables.projectId] });
      toast.success('Contact added to scope');
      return response;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add contact');
    },
  });
}

/**
 * Removes a contact from a project scope
 */
export function useRemoveScopeContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, scopeId, contactId }: {
      projectId: string;
      scopeId: string;
      contactId: string;
    }) => api.scopeContacts.remove(projectId, scopeId, contactId),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project-scopes', variables.projectId] });
      toast.success('Contact removed from scope');
      return response;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to remove contact');
    },
  });
}

/**
 * Sets a contact as the primary contact for a scope
 */
export function useSetPrimaryScopeContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, scopeId, contactId }: {
      projectId: string;
      scopeId: string;
      contactId: string;
    }) => api.scopeContacts.setPrimary(projectId, scopeId, contactId),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project-scopes', variables.projectId] });
      toast.success('Primary contact updated');
      return response;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to set primary contact');
    },
  });
}

/**
 * Sets a contact as the invoice contact for a scope
 */
export function useSetInvoiceScopeContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, scopeId, contactId }: {
      projectId: string;
      scopeId: string;
      contactId: string;
    }) => api.scopeContacts.setInvoice(projectId, scopeId, contactId),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project-scopes', variables.projectId] });
      toast.success('Invoice contact updated');
      return response;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to set invoice contact');
    },
  });
}
